import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import {useCookies} from 'react-cookie';
import {Loader} from '@flexe/ui-components';
import PickConsolidationService from '../../shared/services/PickConsolidationService';
import WarehouseSelector from '../../shared/WarehouseSelector';
import {Reservation, SortOrder, TransitionState, Warehouse} from '../../shared/CommonInterfaces';
import SelectorWithCheckbox from '../../shared/SelectorWithCheckbox';
import WarehouseService from '../../shared/services/WarehouseService';
import BatchWavingService from '../../shared/services/BatchWavingService';
import useSearchParams from '../../shared/useSearchParams';
import {ShipMode} from '../../shared/constants';
import ModeFilter from '../outbound-shipments/shipment-list/ModeFilter';
import MheService from '../../shared/services/MheService';
import {WaveContext} from './WaveContext';
import WavesTable from './WavesTable';
import AlertWavingIssues from './AlertWavingIssues';
import WaveFilter from './WaveFilter';
import {FilterKeys} from './WaveBatchInterfaces';
import CompactStatusTabs from './CompactStatusTabs';
import PaginationButtons from './PaginationButtons';
import SortBy from './SortBy';

const PAGE_SIZE = 50;

interface Props {
  authenticityToken: string;
  carriers: object;
  selectedWarehouse: Warehouse;
  activeWarehouses: Warehouse[];
  hidePrintedFilterEnabled: boolean;
}

const WavesIndex: React.FC<Props> = ({
  authenticityToken,
  selectedWarehouse,
  activeWarehouses,
  carriers,
  hidePrintedFilterEnabled
}) => {
  const pickConsolidationService = useMemo(() => new PickConsolidationService(authenticityToken), [authenticityToken]);
  const mheService = useMemo(() => new MheService(authenticityToken), [authenticityToken]);
  const batchWavingService = useMemo(() => new BatchWavingService(authenticityToken), [authenticityToken]);
  batchWavingService.currentWarehouse = selectedWarehouse.id;
  const warehouseService = useMemo(() => new WarehouseService(authenticityToken), [authenticityToken]);
  const [timerStartsAt, setTimerStartsAt] = useState<Date>(new Date(Date.now()));
  const [errors, setErrors] = useState<string[]>([]);
  const [toRefresh, setToRefresh] = useState<boolean>(false);
  const [alertReloadTrigger, setAlertReloadTrigger] = useState<boolean>(false);

  const [fetchingPage, setFetchingPage] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalWavesCount, setTotalWavesCount] = useState<number>(0);

  const [sortDirection, setSortDirection] = useState<SortOrder>(SortOrder.DESC);
  const ALL_RESERVATIONS_ID_VALUE = -1;
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [reservationId, setReservationId] = useState<number>(ALL_RESERVATIONS_ID_VALUE);
  const [reservationsLoaded, setReservationsLoaded] = useState<boolean>(false);
  const {searchParams, setSearchParam, removeSearchParam} = useSearchParams();
  const [cookies, _setCookie, _removeCookie] = useCookies(['selected_warehouse_id']);

  useEffect(() => {
    if (cookies.selected_warehouse_id && cookies.selected_warehouse_id !== selectedWarehouse.id.toString()) {
      window.location.href = `/wh/waves?warehouse_id=${cookies.selected_warehouse_id}`;
    }
  }, [cookies]);

  const initializeSearchParams = () => {
    // If sort_order is already set, use it to initialize sortDirection
    if (searchParams.has('sort_order')) {
      const sortOrder = searchParams.get('sort_order') as SortOrder;
      if (sortOrder) {
        setSortDirection(sortOrder);
      }
    }

    // Default to Active status if not set
    if (!searchParams.has('status')) {
      setSearchParam(FilterKeys.Status, TransitionState.Active);
    }
  };

  const onSearchParamsUpdated = () => {
    if ((searchParams.get(FilterKeys.SortOrder) as SortOrder) !== sortDirection) {
      setSortDirection(searchParams.get(FilterKeys.SortOrder) as SortOrder);
    }
  };

  useEffect(() => {
    initializeSearchParams();
  }, []);

  useEffect(() => {
    onSearchParamsUpdated();
  }, [searchParams]);

  useEffect(() => {
    setReservationId(ALL_RESERVATIONS_ID_VALUE);
    asyncGetReservations();
  }, [selectedWarehouse.id]);

  const updateSortDirection = (direction: SortOrder) => {
    setSearchParam(FilterKeys.SortOrder, direction);
  };

  const contextValue = useMemo(
    () => ({
      authenticityToken,
      warehouse: selectedWarehouse,
      batchWavingService,
      pickConsolidationService,
      mheService,
      reservationIds: reservationId === ALL_RESERVATIONS_ID_VALUE ? reservations.map((_) => _.id) : [reservationId],
      searchParams,
      carriers: carriers || {},
      reservations,
      refreshing: toRefresh,
      setRefreshing: setToRefresh,
      setErrors
    }),
    [
      batchWavingService,
      pickConsolidationService,
      mheService,
      reservationId,
      searchParams,
      setErrors,
      toRefresh,
      reservations
    ]
  );

  const asyncGetReservations = async () => {
    const response = await warehouseService.getReservationsForWarehouse(selectedWarehouse.id);
    if (response.data && response.data.reservations) {
      setReservations(response.data.reservations);
      setReservationsLoaded(true);
    }
    if (response.errors && response.errors.length > 0) {
      const getReservationsErrors = [].concat(errors);
      response.errors.forEach((_) => {
        getReservationsErrors.push(
          'An error occurred loading the page, please refresh the browser. If the issue persists contact FLEXE support.'
        );
      });
      setErrors(getReservationsErrors);
    }
  };

  const handleModeChange = (newMode: ShipMode) => {
    if (newMode == null) {
      removeSearchParam(FilterKeys.ShipMode);
    } else {
      setSearchParam(FilterKeys.ShipMode, newMode);
    }
  };

  const updateFilterParams = (key: string, value: string) => {
    // Update filterParams if filterParams[key] is set or if there's a value to
    // set for filterParams[key]
    if (searchParams.has(key) || value) {
      if (value) {
        setSearchParam(key, value);
      } else {
        removeSearchParam(key);
      }
    }
  };

  const selectStatusTab = (status: TransitionState) => {
    setSearchParam(FilterKeys.Status, status);
  };

  const reservationDropdown = () => {
    const options = reservations.map((reservation) => {
      return {
        name: reservation.name,
        value: `${reservation.id}`
      };
    });

    options.unshift({name: 'All', value: `${ALL_RESERVATIONS_ID_VALUE}`});

    const onSelect = (value: string) => {
      const selectedId = parseInt(value, 10);
      setReservationId(selectedId);
    };

    return (
      <SelectorWithCheckbox
        options={options}
        selected={[`${reservationId}`]}
        onSelect={onSelect}
        prefixText="Reservation: "
      />
    );
  };

  const reloadPage = () => {
    setTimerStartsAt(new Date());
    setCurrentPage(1);
    setToRefresh(true);
    setAlertReloadTrigger(!alertReloadTrigger);
  };

  const fetchPage = (page: number) => {
    setFetchingPage(true);
    setCurrentPage(page);
  };

  const handleWarehouseSelected = (warehouse: Warehouse) => {
    // NO-OP: Warehouse change now done when cookie changes
    // Will delete this when old WarehouseSelector is deprecated
  };

  return (
    <WaveContext.Provider value={contextValue}>
      <div id="waves-and-batches-index">
        {errors &&
          errors.map((error, idx) => (
            <div className="alert alert-danger" role="alert" key={`error:${idx}`}>
              {error}
            </div>
          ))}
        <div className="title-header">
          <div className={'top-right-controls'}>
            <AlertWavingIssues
              warehouseId={selectedWarehouse.id}
              batchWavingService={batchWavingService}
              reloadTrigger={alertReloadTrigger}
            />
          </div>
          <div>
            <h2>Waves & Batches</h2>
            <div className="filter-control-container">
              <ul className="list-inline">
                <li>{reservationDropdown()}</li>
              </ul>
            </div>
          </div>
          <hr />
          {/* Conditionally build allowedFilterKeys */}
          {(() => {
            const baseAllowedFilterKeys = [
              FilterKeys.WaveId,
              FilterKeys.BatchId,
              FilterKeys.ShipmentId,
              FilterKeys.PurchaseOrder,
              // TODO: FilterKeys.OrderReferenceId,
              FilterKeys.StagingLocation,
              FilterKeys.Carrier,
              FilterKeys.ServiceType,
              FilterKeys.ShipBy,
              FilterKeys.LoadGroup,
              FilterKeys.DestinationTag,
              FilterKeys.Mhe
            ];

            if (hidePrintedFilterEnabled) {
              baseAllowedFilterKeys.push(FilterKeys.HidePrinted);
            }

            return <WaveFilter allowedFilterKeys={baseAllowedFilterKeys} onFilterChange={updateFilterParams} />;
          })()}
        </div>
        <div className="content-container">
          {fetchingPage && (
            <div id="main-loader">
              <Loader loading={true} />
            </div>
          )}
          <div className="control-buttons-row">
            <div className="compact-status-tabs">
              <ModeFilter mode={(searchParams.get('ship_mode') || null) as ShipMode} onModeChange={handleModeChange} />
              <CompactStatusTabs
                statuses={[
                  TransitionState.Active,
                  TransitionState.NotStarted,
                  TransitionState.InProgress,
                  TransitionState.Completed,
                  TransitionState.Cancelled
                ]}
                countsByProcessStatus={{
                  active: null,
                  new: null,
                  not_started: null,
                  in_progress: null
                }}
                selectedTab={(searchParams.get(FilterKeys.Status) || TransitionState.Active) as TransitionState}
                onTabSelect={(status: TransitionState) => selectStatusTab(status)}
              />
            </div>
            <div className="row-group">
              <SortBy onDirectionChange={setSortDirection} />
              <PaginationButtons
                currentPage={currentPage}
                pageSize={PAGE_SIZE}
                setCurrentPage={fetchPage}
                wavesCount={totalWavesCount}
              />
            </div>
          </div>
          {reservationsLoaded && (
            <WavesTable
              pageSize={PAGE_SIZE}
              setFetchingPage={setFetchingPage}
              currentPage={currentPage}
              setTotalWavesCount={setTotalWavesCount}
            />
          )}
        </div>
      </div>
    </WaveContext.Provider>
  );
};

export default WavesIndex;
